/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */
import { jsx, css } from '@emotion/react'
import { Fragment } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import { map } from 'lodash'

import {
  EuiTitle,
  EuiBadge,
  EuiSpacer,
  EuiButtonIcon,
  EuiFlexItem,
  EuiFlexGroup,
  EuiBetaBadge,
  useEuiTheme,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type {
  DeploymentCreateRequest,
  DeploymentResources,
  DeploymentUpdateRequest,
  ElasticsearchClusterTopologyElement,
  InstanceConfiguration,
} from '@modules/cloud-api/v1/types'
import type { AnyTopologyElement, NodeRole } from '@modules/ui-types'

import {
  sanitizeNodeRolesForDisplay,
  getNodeRoles,
} from '@/lib/stackDeployments/selectors/nodeRoles'
import {
  isAutoscaleableTier,
  displayAutoscalingLimitReached,
} from '@/lib/stackDeployments/selectors/autoscaling'
import { getUpsertVersion } from '@/lib/stackDeployments/selectors/creates'
import { getNodeAttributes } from '@/lib/stackDeployments/selectors/topologyElements'
import { getFirstEsCluster } from '@/lib/stackDeployments/selectors/stackDeployment'

import { canDisableConfiguration } from '../../../../../../lib/deployments/architecture'
import { getVersion } from '../../../../../../reducers/clusters'

import InstanceInfoPopover from './InstanceInfoPopover'
import NormalizeSizing from './NormalizeSizing'
import { TopologyElementDescription, getTopologyTitle } from './helpers'

import type { ReactElement, FunctionComponent } from 'react'
import type { NormalizeSizingProps } from './NormalizeSizing'

export type AllProps = {
  deployment: DeploymentCreateRequest | DeploymentUpdateRequest
  topologyElement: AnyTopologyElement
  templateTopologyElement?: AnyTopologyElement
  instanceConfiguration: InstanceConfiguration
  onChange: undefined | ((path: string[], value: any) => void) // path is scoped to the topology element
  isAutoscalingEnabled?: boolean
  dedicatedMasterThreshold?: number
  capMaxNodeCount?: boolean
  betaBadges?: JSX.Element[] // to override the default beta badges if needed
}

const messages = defineMessages({
  hardware: {
    id: 'instance-info-popover.hardware',
    defaultMessage: 'Hardware',
  },
  description: {
    id: 'instance-info-popover.description',
    defaultMessage: 'Description',
  },
  roles: {
    id: 'instance-info-popover.roles',
    defaultMessage: 'Roles',
  },
  autoscaling: {
    id: 'instance-info-popover.autoscaling',
    defaultMessage: 'Autoscaling',
  },
  nodeAttributes: {
    id: 'instance-info-popover.nodeAttributes',
    defaultMessage: 'Node attributes',
  },
  removeAriaLabel: {
    id: 'instance-info-popover.remove.aria-label',
    defaultMessage: 'Remove "{item}"',
  },
})

const Heading: FunctionComponent<AllProps> = ({
  topologyElement,
  templateTopologyElement,
  instanceConfiguration,
  deployment,
  onChange,
  dedicatedMasterThreshold,
  isAutoscalingEnabled,
  betaBadges,
  capMaxNodeCount,
}) => {
  const { formatMessage } = useIntl()
  const { euiTheme } = useEuiTheme()

  const autoscalingLimitReachedStyles = css({
    boxShadow: `inset 0 0 0 1px ${euiTheme.colors.vis.euiColorVis8}`, // $euiColorVis8
    color: euiTheme.colors.vis.euiColorVis8,
  })

  const displayAutoscalingSettings = (): boolean => {
    const version = getUpsertVersion({ deployment }) || undefined
    return Boolean(isAutoscalingEnabled) && isAutoscaleableTier({ topologyElement, version })
  }

  const renderNodeAttributes = (nodeAttributes: Record<string, string>): JSX.Element => {
    const badges = map(nodeAttributes, (v, k) => (
      <EuiFlexItem key={k} grow={false}>
        <EuiBadge data-test-subj='nodeAttribute-badge' data-id={k} key={k} color='hollow'>
          {k}: {v}
        </EuiBadge>
      </EuiFlexItem>
    ))

    return (
      <Fragment>
        <EuiSpacer size='s' />
        <EuiFlexGroup responsive={false} wrap={true} gutterSize='xs'>
          {badges}
        </EuiFlexGroup>
      </Fragment>
    )
  }

  const renderBadges = (): JSX.Element => {
    // not all topology elements have node types but we're duck-typing that property below
    const topologyElementTyped = topologyElement as ElasticsearchClusterTopologyElement

    const esResource = getFirstEsCluster({ deployment })!
    const version = getVersion(esResource)

    const currentInstanceType = instanceConfiguration.instance_type
      ? [instanceConfiguration.instance_type]
      : []

    // the fallbacks here are to still render a badge named for the instance, if the instance has no node types
    const badgeStrings: Array<NodeRole | keyof DeploymentResources | 'coordinating'> =
      instanceConfiguration.instance_type === `elasticsearch`
        ? getNodeRoles({ topologyElement: topologyElementTyped, version })
        : currentInstanceType

    const addCoordinatingBadge =
      instanceConfiguration.instance_type === `elasticsearch` && badgeStrings.includes(`ingest`)

    if (addCoordinatingBadge) {
      badgeStrings.splice(badgeStrings.indexOf(`ingest`), 0, `coordinating`)
    }

    const badges = sanitizeNodeRolesForDisplay(badgeStrings).map((nodeType, i) => (
      <EuiFlexItem key={i} grow={false}>
        <EuiBadge
          data-test-subj='topologyElement-badge'
          data-id={nodeType}
          key={nodeType}
          color='hollow'
        >
          {nodeType}
        </EuiBadge>
      </EuiFlexItem>
    ))

    return (
      <Fragment>
        <EuiSpacer size='s' />
        <EuiFlexGroup responsive={false} wrap={true} gutterSize='xs'>
          {badges}
        </EuiFlexGroup>
      </Fragment>
    )
  }

  const renderAutoscalingLimitWarning = ({
    size,
    autoscalingMaxSize,
    autoscalingMinSize,
  }: NormalizeSizingProps): ReactElement | null => {
    if (!autoscalingMaxSize) {
      return null
    }

    const limitReached = displayAutoscalingLimitReached({
      size,
      autoscalingMax: autoscalingMaxSize,
      autoscalingMin: autoscalingMinSize,
    })

    if (!limitReached) {
      return null
    }

    return (
      <EuiFlexItem grow={false}>
        <EuiBetaBadge
          css={autoscalingLimitReachedStyles}
          data-test-id='autoscaling-limit-reached'
          color='accent'
          // style={{ display: 'inherit' }} // to avoid built-in bottom margin that pushes it up too high
          label={
            <FormattedMessage
              id='autoscaling-edit-settings.limit-reached'
              defaultMessage='Limit Reached'
            />
          }
          tooltipContent={
            <FormattedMessage
              id='autoscaling-edit-settings.limit-reached.tooltip'
              defaultMessage='To continue experiencing optimal performance, we recommend increasing your maximum size per zone.'
            />
          }
        />
      </EuiFlexItem>
    )
  }

  const configVersion = instanceConfiguration.config_version
  const icName = instanceConfiguration.name || ``
  const icTitle = `${icName}${configVersion !== undefined ? ` (version ${configVersion})` : ``}`

  const listItems = [
    {
      title: formatMessage(messages.hardware),
      description: icTitle,
      dataTestId: `instance-info-popover-hardware`,
    },
    {
      title: formatMessage(messages.description),
      description: instanceConfiguration.description || ``,
    },
    {
      title: formatMessage(messages.roles),
      description: renderBadges(),
    },
  ]

  const nodeAttributes = getNodeAttributes({ topologyElement })

  if (nodeAttributes) {
    listItems.push({
      title: formatMessage(messages.nodeAttributes),
      description: renderNodeAttributes(nodeAttributes),
    })
  }

  const version = getUpsertVersion({ deployment })

  const showDisableButton =
    onChange &&
    canDisableConfiguration({
      topologyElement,
      sliderInstanceType: instanceConfiguration.instance_type,
      dedicatedMasterThreshold,
    }) &&
    (!isAutoscalingEnabled || !isAutoscaleableTier({ topologyElement, version }))

  const title = getTopologyTitle(
    templateTopologyElement || topologyElement,
    instanceConfiguration,
    version,
    formatMessage,
  )

  return (
    <Fragment>
      <EuiFlexGroup responsive={false} alignItems='center' gutterSize='s'>
        <EuiFlexItem>
          <EuiFlexGroup gutterSize='s' responsive={false} alignItems='center'>
            <EuiFlexItem grow={false}>
              <EuiTitle size='xxs'>
                <h4>{title}</h4>
              </EuiTitle>
            </EuiFlexItem>
            <EuiFlexItem
              data-test-id='info-instance-popover'
              data-id={instanceConfiguration.name}
              grow={false}
            >
              <InstanceInfoPopover listItems={listItems} />
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          {betaBadges ||
            (displayAutoscalingSettings() && (
              <EuiFlexGroup gutterSize='s'>
                <EuiFlexItem grow={false}>
                  <EuiBetaBadge
                    data-test-id='autoscaling-badge'
                    label={formatMessage(messages.autoscaling)}
                  />
                </EuiFlexItem>
                <NormalizeSizing
                  topologyElement={topologyElement}
                  instanceConfiguration={instanceConfiguration}
                  onChange={onChange}
                  capMaxNodeCount={capMaxNodeCount}
                  autoscalingSizeFilter='constrainToLimits'
                >
                  {renderAutoscalingLimitWarning}
                </NormalizeSizing>
              </EuiFlexGroup>
            ))}
        </EuiFlexItem>
        {showDisableButton && (
          <NormalizeSizing
            topologyElement={topologyElement}
            instanceConfiguration={instanceConfiguration}
            onChange={onChange}
            capMaxNodeCount={capMaxNodeCount}
            autoscalingSizeFilter='constrainToLimits'
          >
            {({ onChangeSize }) => (
              <EuiFlexItem grow={false}>
                <EuiButtonIcon
                  data-test-id='topologyElement-remove'
                  iconType='cross'
                  color='danger'
                  onClick={onChangeSize && (() => onChangeSize(0))}
                  aria-label={formatMessage(messages.removeAriaLabel, { item: title })}
                />
              </EuiFlexItem>
            )}
          </NormalizeSizing>
        )}
      </EuiFlexGroup>

      <TopologyElementDescription
        topologyElement={templateTopologyElement || topologyElement}
        instanceConfiguration={instanceConfiguration}
        version={version}
      />
    </Fragment>
  )
}

export default Heading
